/**
 * useAuth — session state for the dashboard: login/register/logout, the
 * login activity log. The Console has NO RBAC and exactly one view, so there is
 * no view-mode gating here: ADR 0004 / E06 retired the operator/research dual view
 * and it has been removed.
 *
 * Owns everything App.jsx previously held about auth. The login log prints
 * REAL events only (authenticate, token received, ready); the legacy fake
 * boot sequence is gone by design (see .impeccable.md: honest by default).
 *
 * Offline mode (VITE_OFFLINE_AUTH=true) bypasses the backend for login only.
 *
 * F14 adds the second half of the credential lifecycle: the /reset-password
 * landing (routerless, same mechanism as L01's /verify-email), the recovery
 * view toggle, and the two post-mutation sign-outs. Both password mutations now
 * END the session that made them (the backend increments
 * `users.credential_version`), so both finish on the login screen carrying a
 * `loginNotice` that says why.
 */

import { useState, useEffect, useCallback, useRef } from "react";
import * as api from "../engine/api";
import { getSessionKey } from "../engine/auth";
import { beginSession, endSession } from "../engine/visitedScreens";
import { USE_API } from "../engine/env";
import useResendCooldown from "./useResendCooldown";

// Small pause so the final "Ready" log line is visible before the shell mounts.
const READY_DELAY_MS = 300;

// L01 — the magic-link landing path. The Console has NO router, so we detect it
// from window.location on load and route to the VerifyEmail view (see App.jsx).
const VERIFY_PATH = "/verify-email";

// F14 — the password-reset landing path. Same routerless mechanism as L01's
// magic link: read once from window.location on load and route to the
// ResetPassword view (see App.jsx). Adding a router for two landing paths would
// be a far larger change than the paths are worth.
const RESET_PATH = "/reset-password";

/**
 * Read a `?token=<raw>` landing context for `path` from the current URL
 * (routerless SPA). Shared by the L01 verify-email link and the F14 reset link,
 * which have identical shapes; keeping one reader means a fix to the parsing
 * cannot land on one path and miss the other.
 */
function readTokenLanding(path) {
  if (typeof window === "undefined" || !window.location) return { onPath: false, token: "" };
  const onPath = window.location.pathname === path;
  if (!onPath) return { onPath: false, token: "" };
  const token = new URLSearchParams(window.location.search).get("token") || "";
  return { onPath: true, token };
}

/** Read the verify-email landing context from the current URL (routerless SPA). */
function readVerifyContext() {
  return readTokenLanding(VERIFY_PATH);
}

/** Read the password-reset landing context from the current URL (F14 FR8). */
function readResetContext() {
  return readTokenLanding(RESET_PATH);
}

export default function useAuth() {
  const [loggedIn, setLoggedIn] = useState(() => USE_API && api.hasToken());
  const [user, setUser] = useState("");
  // OMEGA-814 — what the Console CALLS the customer, as distinct from what it signs them
  // in with. `user` is the login-form field and therefore holds the login identity (an
  // email, for any account created from migration 091 onward). Rendering that in the
  // header is a demotion: the customer gave us a name at registration and the UI should
  // use it. Kept as separate state rather than derived from `user` because the two answer
  // different questions and only one of them is typed at a keyboard.
  const [displayName, setDisplayName] = useState("");
  const [password, setPassword] = useState("");
  const [loginLog, setLoginLog] = useState([]);
  const [loginError, setLoginError] = useState("");
  // L01 — a 403 email_not_verified is a recoverable state (resend the link), not
  // a wrong-credentials failure; the login screen renders it distinctly.
  const [needsVerification, setNeedsVerification] = useState(false);
  // OMEGA-563 — the login-screen resend had NO guard at all (spammable). Add an
  // in-flight lock plus the shared cooldown so a rapid re-click can't fire the
  // real outbound-email endpoint the backend would only silently drop.
  const [resendingVerification, setResendingVerification] = useState(false);
  const {
    remaining: resendCooldownRemaining,
    onCooldown: resendOnCooldown,
    start: startResendCooldown,
  } = useResendCooldown();
  // Unauthenticated branch toggles between the login and registration (F02) screens.
  const [authView, setAuthView] = useState("login");

  // L01 — verify-email magic-link entry, resolved once from the landing URL.
  const [verifyPath, setVerifyPath] = useState(() => readVerifyContext().onPath);
  const [verifyToken, setVerifyToken] = useState(() => readVerifyContext().token);

  // F14 — password-reset link entry, resolved once from the landing URL. The raw
  // token lives here and in the ResetPassword component only; it is never stored.
  const [resetPath, setResetPath] = useState(() => readResetContext().onPath);
  const [resetToken, setResetToken] = useState(() => readResetContext().token);

  // A one-shot neutral message shown ON the login screen, for the cases where
  // the thing worth telling the user happened on a screen we are about to leave:
  // a completed reset (FR10) and a completed authenticated change (FR11) both
  // end with the session gone, so their success copy cannot live where the
  // action was taken. Distinct from loginError — this is not a failure.
  const [loginNotice, setLoginNotice] = useState("");

  // Login is in flight. Exposed so LoginScreen can disable the Connect action
  // (visibility of status + duplicate-submission prevention). This UI guard is
  // defense in depth only — the loginGen guard below is the load-bearing
  // correctness fix and holds even if this flag is bypassed.
  const [authenticating, setAuthenticating] = useState(false);

  // Login-attempt generation — the login-SUCCESS analogue of the expiry-path
  // token-ownership guard. Out-of-order completion must NOT cross sessions: if
  // attempt B is started while A is still pending, A resolving LATE must not
  // overwrite B's token, resurrect A's identity, or re-schedule readiness.
  // Each doLogin() bumps this ref and captures its value; only the attempt whose
  // captured generation still equals loginGen.current at resolve time may commit
  // its token / flip loggedIn / schedule readiness. A ref (not state) so the
  // check is synchronous and always reflects the latest attempt.
  const loginGen = useRef(0);

  // The pending "Ready" delay before setLoggedIn(true). Held in a ref so logout,
  // a session-expiry, a fresh login attempt, or unmount can CANCEL it: without
  // this, an expiry during the 300ms window would race — logout sets loggedIn
  // false, then the stale timer flips it back true (authenticated with no
  // credential). scheduleReady() always cancels any prior timer first.
  const readyTimer = useRef(null);
  const cancelReadyTimer = useCallback(() => {
    if (readyTimer.current !== null) {
      clearTimeout(readyTimer.current);
      readyTimer.current = null;
    }
  }, []);
  const scheduleReady = useCallback(() => {
    cancelReadyTimer();
    readyTimer.current = setTimeout(() => {
      readyTimer.current = null;
      setLoggedIn(true);
    }, READY_DELAY_MS);
  }, [cancelReadyTimer]);

  // Invalidate any pending ready-timer if the hook unmounts mid-delay.
  useEffect(() => cancelReadyTimer, [cancelReadyTimer]);

  const addLog = useCallback((m) => {
    setLoginLog((p) => [...p.slice(-16), `[${new Date().toISOString().split("T")[1].split(".")[0]}] ${m}`]);
  }, []);

  const doLogin = useCallback(async () => {
    setLoginError("");
    setNeedsVerification(false);
    // A "your password was changed" notice describes the PREVIOUS action; once
    // the user acts on it by signing in, it stops being current.
    setLoginNotice("");

    if (!user || !password) {
      // OMEGA-814 — the login field is the customer's EMAIL. `user` keeps its name
      // because it is also the offline-auth display value and the session-key fallback;
      // what changed is what a customer types into it, not what it is for.
      setLoginError("Email and password are required");
      return;
    }

    // Defense in depth: ignore an overlapping submit while a login is in flight
    // so the login screen cannot fire duplicate attempts. The generation guard
    // below is what actually enforces correctness if this is ever bypassed.
    if (authenticating) return;

    // Claim a new generation for THIS attempt; the awaited outcome is committed
    // only if this generation is still the current one at resolve time.
    loginGen.current += 1;
    const gen = loginGen.current;
    setAuthenticating(true);
    // A fresh, PROCEEDING attempt supersedes any pending ready-delay from a prior
    // login. Cancel ONLY now that we've committed to this attempt: an invocation
    // that early-returned above (in flight, or blank fields) must NOT cancel the
    // in-flight winning attempt's ready timer — doing so before the guards would
    // deadlock login (loggedIn=false + authenticating=true, no timer to recover).
    cancelReadyTimer();

    addLog(`AUTH: Authenticating '${user}'...`);

    if (USE_API) {
      try {
        const resp = await api.login(user, password);
        // Latest-attempt-wins: a superseded (older) login resolving is a no-op —
        // do NOT persist its token, flip loggedIn, or schedule readiness.
        if (loginGen.current !== gen) return;
        // The winning attempt owns token persistence (api.login is thin
        // transport now); commit the JWT here so a stale attempt can't clobber it.
        api.setToken(resp.access_token);
        addLog("SEC: JWT token received");
        addLog("CONSOLE: Ready. Welcome " + resp.username);
        scheduleReady();
        return;
      } catch (err) {
        // A superseded attempt failing must not overwrite the winner's UI state.
        if (loginGen.current !== gen) return;
        setAuthenticating(false);
        addLog(`AUTH: FAIL ${err.message}`);
        // L01 FR9 — an unverified account is a 403 with a fixed error code;
        // surface a check-your-inbox message + resend affordance, distinct from a
        // 401. OMEGA-590 — key off the stable envelope `code` (readDetail now
        // returns the human `message`, not this machine code).
        if (err.code === "email_not_verified") {
          setNeedsVerification(true);
          setLoginError(
            "Your email is not verified yet. Check your inbox for the verification link, or resend it below.",
          );
        } else {
          setLoginError(err.message);
        }
        return;
      }
    }

    // Offline mode is opt-in only via VITE_OFFLINE_AUTH=true.
    if (loginGen.current !== gen) return;
    addLog("AUTH: Offline mode (no backend)");
    addLog("CONSOLE: Ready. Welcome " + user);
    scheduleReady();
  }, [user, password, authenticating, addLog, cancelReadyTimer, scheduleReady]);

  // L01 — resend the verification link for the identifier currently typed into the
  // login form (OMEGA-814: an email). Always succeeds server-side (202, no
  // enumeration); we surface a single neutral confirmation regardless. That silence is
  // why the key this is sent under has to match what the backend resolves on — a
  // mismatch here would look identical to success.
  const resendVerification = useCallback(async () => {
    if (!user || resendingVerification || resendOnCooldown) return;
    setResendingVerification(true);
    try {
      await api.resendVerification(user);
      setLoginError("If that account awaits verification, a fresh link is on its way. Check your inbox.");
      startResendCooldown();
    } catch (err) {
      setLoginError(err.message);
    } finally {
      setResendingVerification(false);
    }
  }, [user, resendingVerification, resendOnCooldown, startResendCooldown]);

  const logout = useCallback(() => {
    // Kill any pending ready-delay first so it cannot flip loggedIn back true
    // after we reset the session (the expiry handler routes through here too).
    cancelReadyTimer();
    // Supersede any login still in flight so its late resolution is a no-op
    // (it must not persist a token or resurrect the session after logout).
    loginGen.current += 1;
    setAuthenticating(false);
    api.clearToken();
    setLoggedIn(false);
    setUser("");
    setDisplayName("");
    setPassword("");
    setLoginLog([]);
    setLoginError("");
    setNeedsVerification(false);
    setAuthView("login");
    // Session-scoped client state dies with the session. The onboarding
    // checklist's visited-screens record is per-customer, so leaving it behind
    // would tick a step for whoever logs in next on this tab. The expiry handler
    // below routes through here, so this covers both exits.
    endSession();
  }, [cancelReadyTimer]);

  // Bind the visited-screens record to the authenticated customer. One effect
  // rather than a call at each of the three login sites, and it clears only when
  // the OWNER changes, so it is a no-op on re-render within a session. This is
  // the backstop for the clear in logout(): even if a future auth path returned
  // to the console without passing through logout, the record could not carry
  // into a different customer's session.
  //
  // The owner comes from the CREDENTIAL, not from `user`. `loggedIn` is seeded
  // from api.hasToken() and so survives a reload; `user` is written only by the
  // login form and so does not. Keying off `user` left an ordinary authenticated
  // refresh (loggedIn=true, user="") with no owner at all, and markVisited drops
  // every visit while the record is unowned, so the checklist's Billing step
  // could never tick again after a refresh. `user` remains the fallback for
  // offline-auth mode, which has no token to read an identity from.
  //
  // Only the OWNERSHIP is re-established here. The visits themselves are still
  // lost on reload, by construction rather than by a call anyone could forget:
  // visitedScreens' Set is page-lifetime memory and a reload re-evaluates the
  // module. Losing them understates what the customer did, which is the right
  // direction to fail (see that module's header).
  useEffect(() => {
    if (!loggedIn) return;
    beginSession(getSessionKey(api.getToken()) || user);
  }, [loggedIn, user]);

  // OMEGA-814 — resolve the display name from the profile whenever a session becomes
  // live. Keyed on `loggedIn` for exactly the reason the effect above is: `loggedIn` is
  // seeded from api.hasToken() and so survives a reload, while `user` is written only by
  // the login form and does not. Deriving the header from `user` would leave every
  // authenticated REFRESH with no identity at all, which is the bug this keying avoids.
  //
  // `full_name` is NULL for any account created before migration 091, so the fallback
  // chain ends at the username — the same honest degradation ProfilePanel makes. A failed
  // fetch is swallowed: the header falls back to the login identity, which is strictly
  // better than an error boundary over a decorative label.
  useEffect(() => {
    if (!loggedIn || !USE_API) return undefined;
    let cancelled = false;
    api
      .getProfile()
      .then((profile) => {
        if (!cancelled) setDisplayName(profile?.full_name || profile?.username || "");
      })
      .catch(() => {
        if (!cancelled) setDisplayName("");
      });
    // Guards against a logout (or a second sign-in) landing mid-flight and writing the
    // previous customer's name into the new session.
    return () => {
      cancelled = true;
    };
  }, [loggedIn]);

  // OMEGA-514 — session-expiry auto-logout. api.js fires SESSION_EXPIRED_EVENT
  // only when an ALREADY-authenticated call's credential is rejected (an
  // expired/invalid JWT or a revoked API key), NOT on a login or change-password
  // 401. The token is already cleared by api.js; logout() resets the rest of the
  // session state so App re-renders the login screen (routerless redirect).
  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    const evt = api.SESSION_EXPIRED_EVENT || "omega:session-expired";
    const onExpired = () => logout();
    window.addEventListener(evt, onExpired);
    return () => window.removeEventListener(evt, onExpired);
  }, [logout]);

  // F14 FR11/FR12 — an authenticated password change revoked every JWT for this
  // account, the one in this browser included. api.js has already dropped the
  // token and fired PASSWORD_CHANGED_EVENT; reset the rest of the session state
  // and explain WHY the user is back at the login screen. Deliberately a second
  // listener rather than a branch inside the expiry one: the two events mean
  // different things to the user, and only this one can promise the new
  // password works.
  useEffect(() => {
    if (typeof window === "undefined" || !window.addEventListener) return undefined;
    const evt = api.PASSWORD_CHANGED_EVENT || "omega:password-changed";
    const onChanged = () => {
      logout();
      setLoginNotice("Your password was changed and every signed-in session was ended. Sign in with your new password.");
    };
    window.addEventListener(evt, onChanged);
    return () => window.removeEventListener(evt, onChanged);
  }, [logout]);

  // F14 — drop the /reset-password landing and restore a clean URL. Doing this
  // is what removes the raw reset token from the address bar and from the
  // history entry (FR10), so it is not merely cosmetic routing.
  const clearReset = useCallback(() => {
    setResetPath(false);
    setResetToken("");
    if (typeof window !== "undefined" && window.history && window.history.replaceState) {
      window.history.replaceState({}, "", "/");
    }
  }, []);

  // F14 FR10 — the reset succeeded. Scrub the token-bearing URL and tear down any
  // session in this browser IMMEDIATELY (it was minted before the credential
  // version moved, so it is already dead server-side), but leave `resetPath`
  // standing so the success message the user is reading does not vanish
  // underneath them. They leave via leaveReset when they choose to.
  const completeReset = useCallback(() => {
    logout();
    if (typeof window !== "undefined" && window.history && window.history.replaceState) {
      window.history.replaceState({}, "", "/");
    }
    setLoginNotice("Your password has been reset. Sign in with your new password.");
  }, [logout]);

  // F14 FR8/FR14 — leave the reset screen for the login screen.
  const leaveReset = useCallback(() => {
    clearReset();
    setAuthView("login");
  }, [clearReset]);

  // F14 FR14 — the link was invalid, expired or already used, so send the user
  // to the recovery form to request a fresh one rather than to a login screen
  // that cannot help them.
  const requestNewResetLink = useCallback(() => {
    clearReset();
    setLoginError("");
    setLoginNotice("");
    setNeedsVerification(false);
    setAuthView("forgot");
  }, [clearReset]);

  // L01 — leave the /verify-email landing view: drop it and restore a clean URL
  // so the routerless SPA re-renders the login/console branch normally.
  const clearVerify = useCallback(() => {
    setVerifyPath(false);
    setVerifyToken("");
    if (typeof window !== "undefined" && window.history && window.history.replaceState) {
      window.history.replaceState({}, "", "/");
    }
  }, []);

  // L01 FR8 — verify-email succeeded and stored the JWT; reuse the same
  // loggedIn path as login so the freshly-verified user lands in their default
  // view (auto-login on verify). A first-time verified user still has
  // onboarded_at=null, so F12 onboarding shows as normal.
  const handleVerified = useCallback((resp) => {
    setUser(resp?.username || "");
    clearVerify();
    setAuthView("login");
    setLoggedIn(true);
  }, [clearVerify]);

  // L01 — verify failed (invalid/expired/used); return to the login screen.
  const leaveVerify = useCallback(() => {
    clearVerify();
    setAuthView("login");
  }, [clearVerify]);

  // F02/L01: on the flag-off (dev) path api.register stores a JWT and returns a
  // TokenResponse; reuse the login path so the new user lands in their default
  // view. Under the deployed default (verification required) RegisterScreen shows
  // the check-your-inbox state instead and never calls this.
  const handleRegistered = useCallback((resp) => {
    setUser(resp?.username || "");
    setAuthView("login");
    setLoggedIn(true);
  }, []);

  // Switch the unauthenticated branch to the registration screen, dropping
  // any stale login error / verification notice so it does not leak onto the new form.
  const openRegister = useCallback(() => {
    setLoginError("");
    setNeedsVerification(false);
    setLoginNotice("");
    setAuthView("register");
  }, []);

  // F14 FR2 — open the recovery view from the login screen. Same state hygiene as
  // openRegister: a login failure or verification notice belongs to the form the
  // user just left, and carrying it onto the recovery form would read as a
  // failure of the recovery request itself.
  const openForgot = useCallback(() => {
    setLoginError("");
    setNeedsVerification(false);
    setLoginNotice("");
    setAuthView("forgot");
  }, []);

  // F14 FR2 — the recovery view's route back to login.
  const closeForgot = useCallback(() => {
    setLoginError("");
    setLoginNotice("");
    setAuthView("login");
  }, []);

  return {
    loggedIn,
    authenticating,
    user,
    setUser,
    displayName,
    password,
    setPassword,
    loginLog,
    loginError,
    loginNotice,
    needsVerification,
    authView,
    setAuthView,
    verifyPath,
    verifyToken,
    resetPath,
    resetToken,
    doLogin,
    logout,
    handleRegistered,
    handleVerified,
    leaveVerify,
    resendVerification,
    resendingVerification,
    resendCooldownRemaining,
    resendOnCooldown,
    openRegister,
    openForgot,
    closeForgot,
    completeReset,
    leaveReset,
    requestNewResetLink,
  };
}
